import React from "react";
import "./WelocatePriceFilter.css";

const MIN = 0;
const MAX = 2500000;
const STEP = 25000;

const PRESETS = [
  { label: "Under €400k", min: 0, max: 400000 },
  { label: "€400k – €700k", min: 400000, max: 700000 },
  { label: "€700k – €1M", min: 700000, max: 1000000 },
  { label: "€1M+", min: 1000000, max: MAX, wide: true },
];

const WelocatePriceFilter = ({
  range,
  onRangeChange,
  onApply,
  onClose,
}) => {
  const setRange = onRangeChange;

  const handleSliderChange = (e, index) => {
    const value = Number(e.target.value);
    setRange((prev) => {
      const next = [...prev];
      next[index] = value;
      if (index === 0 && value > next[1]) next[1] = value;
      if (index === 1 && value < next[0]) next[0] = value;
      return next;
    });
  };

  const handleInputChange = (e, index) => {
    const raw = e.target.value.replace(/\D/g, "");
    const fallback = index === 0 ? MIN : MAX;
    const value = raw === "" ? fallback : Math.min(Math.max(Number(raw), MIN), MAX);
    setRange((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handlePreset = (preset) => {
    setRange([preset.min, preset.max]);
    onApply?.(preset.min, preset.max);
  };

  const isPresetActive = (preset) =>
    range[0] === preset.min && range[1] === preset.max;

  const formatMobileValue = (v, index) => {
    const fallback = index === 0 ? MIN : MAX;
    return v === fallback ? "" : v.toLocaleString("nl-NL");
  };

  const minFraction = (range[0] - MIN) / (MAX - MIN);
  const maxFraction = (range[1] - MIN) / (MAX - MIN);

  return (
    <div className="wl-price-filter">
      <div className="wl-price-filter__header">
        <h3 className="wl-price-filter__title">Price range</h3>
        <button
          type="button"
          className="wl-price-filter__header-reset"
          onClick={() => {
            setRange([MIN, MAX]);
            onApply?.(null, null);
          }}
        >
          Reset
        </button>
      </div>
      <p className="wl-price-filter__lede">Set a minimum, a maximum, or both</p>

      <div className="wl-price-filter__track-wrapper">
        <div
          className="wl-price-filter__track-fill"
          style={{
            left: `${minFraction * 100}%`,
            right: `${(1 - maxFraction) * 100}%`,
          }}
        />
        <input
          type="range"
          className="wl-price-filter__thumb"
          min={MIN}
          max={MAX}
          step={STEP}
          value={range[0]}
          onChange={(e) => handleSliderChange(e, 0)}
        />
        <input
          type="range"
          className="wl-price-filter__thumb"
          min={MIN}
          max={MAX}
          step={STEP}
          value={range[1]}
          onChange={(e) => handleSliderChange(e, 1)}
        />
      </div>

      <div className="wl-price-filter__inputs">
        <div className="wl-price-filter__input-group">
          <label className="wl-price-filter__label">Min</label>
          <div className="wl-price-filter__input-wrapper">
            <span className="wl-price-filter__currency">€</span>
            <input
              type="text"
              inputMode="numeric"
              className="wl-price-filter__input wl-price-filter__input--desktop"
              value={range[0].toLocaleString("nl-NL")}
              onChange={(e) => handleInputChange(e, 0)}
            />
            <input
              type="text"
              inputMode="numeric"
              className="wl-price-filter__input wl-price-filter__input--mobile"
              placeholder="No min"
              value={formatMobileValue(range[0], 0)}
              onChange={(e) => handleInputChange(e, 0)}
            />
          </div>
        </div>

        <div className="wl-price-filter__divider" />
        <span className="wl-price-filter__dash">—</span>

        <div className="wl-price-filter__input-group">
          <label className="wl-price-filter__label">Max</label>
          <div className="wl-price-filter__input-wrapper">
            <span className="wl-price-filter__currency">€</span>
            <input
              type="text"
              inputMode="numeric"
              className="wl-price-filter__input wl-price-filter__input--desktop"
              value={range[1].toLocaleString("nl-NL")}
              onChange={(e) => handleInputChange(e, 1)}
            />
            <input
              type="text"
              inputMode="numeric"
              className="wl-price-filter__input wl-price-filter__input--mobile"
              placeholder="No max"
              value={formatMobileValue(range[1], 1)}
              onChange={(e) => handleInputChange(e, 1)}
            />
          </div>
        </div>
      </div>

      <div className="wl-price-filter__presets">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className={`wl-price-filter__preset${
              isPresetActive(preset) ? " wl-price-filter__preset--active" : ""
            }${preset.wide ? " wl-price-filter__preset--wide" : ""}`}
            onClick={() => handlePreset(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="wl-price-filter__actions">
        <button className="wl-price-filter__reset" onClick={() => { setRange([MIN, MAX]); onApply?.(null, null); }}>Reset</button>
        <button className="wl-price-filter__apply" onClick={() => onApply?.(range[0], range[1])}>Apply</button>
      </div>

      <button
        type="button"
        className="wl-price-filter__show-all"
        onClick={() => {
          onApply?.(range[0], range[1]);
          onClose?.();
        }}
      >
        Submit
      </button>
    </div>
  );
};

export default WelocatePriceFilter;
